"use client"

import { useTranslations } from "next-intl"

import { ClaimLockup } from "@/components/marketplace/bnpl/claim-lockup"
import { ArrowIcon, Button, NeloMarkIcon } from "@/components/ui"
import { LocalizedClientLink } from "@/components/ui/localized-client-link"
import { ROUTES } from "@/lib/routes"
import { tv, type VariantProps } from "@/lib/utils/tv"

// The prototype's `.barra`: blue field, white copy, white pill, and the o of the
// wordmark blown up behind it at low opacity. Bled off three edges on purpose —
// cropped this hard the two open arcs read as a deliberate mark, where a whole o
// floating at 15% only reads as a smudge.
const band = tv({
  slots: {
    root: "bg-brand-field @container relative isolate overflow-hidden",
    content: "flex w-full flex-wrap items-center",
    mark: "text-fg-inverted pointer-events-none absolute top-1/2 -left-16 -z-1 size-56 -translate-y-1/2 opacity-12",
    lockups: "text-fg-inverted flex flex-wrap items-center gap-x-500 gap-y-200",
    // Full white, not on-scrim-muted: white at 0.55 over brand-field measures
    // 3.73:1, under AA. The lockups stay louder by weight, not by opacity.
    copy: "font-text text-on-scrim min-w-0 text-pretty",
    pill: "press text-fg-brand shrink-0 font-bold",
  },
  variants: {
    containment: {
      inset: {
        root: "rounded-12x",
        content: "gap-x-600 gap-y-300 px-500 py-400 @3xl:gap-y-400 @3xl:px-700 @3xl:py-500",
        copy: "text-body-sm basis-full @3xl:text-body @3xl:basis-auto @3xl:flex-1",
        pill: "w-full justify-center @xl:ml-auto @xl:w-auto",
      },
      // One item per row until there is room for the sentence beside the claim,
      // which is the prototype's own mobile barra. Sharing a row below that put
      // the sentence in a 250px column and wrapped «0% de interés» in half.
      bleed: {
        content: "max-w-page px-gut mx-auto gap-x-700 gap-y-400 py-700 @3xl:gap-y-500 @3xl:py-800",
        lockups: "basis-full @3xl:basis-auto",
        copy: "text-body-lg basis-full @3xl:max-w-measure-lg @3xl:flex-1",
        pill: "w-full max-w-100 justify-center @3xl:ml-auto @3xl:w-auto",
      },
    },
  },
  defaultVariants: { containment: "inset" },
})

type BnplBandProps = VariantProps<typeof band> & { className?: string }

export const BnplBand = ({ containment, className }: BnplBandProps) => {
  const t = useTranslations("marketplace.bnpl.band")
  const bleed = containment === "bleed"
  const styles = band({ containment })
  return (
    <section className={styles.root({ className })}>
      <NeloMarkIcon aria-hidden className={styles.mark()} />
      <div className={styles.content()}>
        <div className={styles.lockups()}>
          <ClaimLockup variant="cero" size={bleed ? "lg" : "md"} onBrand />
          <ClaimLockup variant="hoy" size={bleed ? "lg" : "md"} onBrand />
        </div>
        <p className={styles.copy()}>{t(bleed ? "sentence" : "sub")}</p>
        <Button asChild variant="inverse" size="md" className={styles.pill()}>
          <LocalizedClientLink href={`/${ROUTES.HOW_TO_BUY}`}>
            {t("cta")}
            <ArrowIcon size={16} />
          </LocalizedClientLink>
        </Button>
      </div>
    </section>
  )
}
